// src/components/admin/scheduling/components/CreateEventModal.js
import React, { useState, useEffect, useCallback } from "react";
import "./EventModal.css";
import { minutesToTimeStr, timeStrToMinutes } from "../timeUtils";

const DAY_CHIPS = [
  { key: "sunday",    label: "S" },
  { key: "monday",    label: "M" },
  { key: "tuesday",   label: "T" },
  { key: "wednesday", label: "W" },
  { key: "thursday",  label: "T" },
  { key: "friday",    label: "F" },
  { key: "saturday",  label: "S" },
];

// Common event types for quick title generation
const EVENT_TYPES = [
  "SAT DT", "PSAT DT", "ACT DT", "CAT DT",
  "SAT IT", "PSAT IT", "ACT IT", "CAT IT",
  "DC", "Interview", "Clearing Service",
];

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dateToKey(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return todayKey();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayOfMonthFromDateKey(dateKey) {
  const parts = (dateKey || "").split("-");
  return parts.length === 3 ? parseInt(parts[2], 10) : null;
}

function dowFromDateKey(dateKey) {
  if (!dateKey) return null;
  const [y, m, d] = dateKey.split("-").map(Number);
  return ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][new Date(y, m - 1, d).getDay()];
}

export default function CreateEventModal({ isOpen, onClose, ctx, onCreate }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [startTimeStr, setStartTimeStr] = useState("09:00");
  const [endTimeStr, setEndTimeStr] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [repeatOption, setRepeatOption] = useState("never");
  // Quick-fill helpers: when both quickType and quickName are set, title is auto-generated
  const [quickType, setQuickType] = useState("");
  const [quickName, setQuickName] = useState("");
  const [titleAutoFilled, setTitleAutoFilled] = useState(false);

  const [recurFrequency, setRecurFrequency] = useState("weekly");
  const [recurInterval, setRecurInterval] = useState(1);
  const [recurWeekDays, setRecurWeekDays] = useState([]);
  const [recurEndsType, setRecurEndsType] = useState("never");
  const [recurEndDate, setRecurEndDate] = useState("");
  const [recurOccurrences, setRecurOccurrences] = useState(13);

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle("");
    setNotes("");
    setRepeatOption("never");
    setQuickType("");
    setQuickName("");
    setTitleAutoFilled(false);
    setRecurFrequency("weekly");
    setRecurInterval(1);
    setRecurEndsType("never");
    setRecurEndDate("");
    setRecurOccurrences(13);
    setErrors({});

    const initialDate = ctx?.date ? dateToKey(ctx.date) : todayKey();
    setDate(initialDate);
    setStartTimeStr(minutesToTimeStr(ctx?.startMin ?? 540));
    setEndTimeStr(minutesToTimeStr(ctx?.endMin ?? 600));

    const dow = dowFromDateKey(initialDate);
    setRecurWeekDays(dow ? [dow] : []);
  }, [isOpen, ctx]); // eslint-disable-line react-hooks/exhaustive-deps

  const startTime = timeStrToMinutes(startTimeStr);
  const endTime   = timeStrToMinutes(endTimeStr);

  const validate = useCallback(() => {
    const errs = {};
    if (!title.trim()) errs.title = "Title is required.";
    if (!date) errs.date = "Date is required.";
    const st = timeStrToMinutes(startTimeStr);
    const et = timeStrToMinutes(endTimeStr);
    if (et <= st) errs.endTime = "End time must be after start time.";
    if (repeatOption === "custom") {
      if (!Number.isInteger(Number(recurInterval)) || Number(recurInterval) < 1)
        errs.recurInterval = "Must be a positive whole number.";
      if (recurFrequency === "weekly" && recurWeekDays.length === 0)
        errs.recurWeekDays = "Select at least one day.";
      if (recurEndsType === "on") {
        if (!recurEndDate) errs.recurEndDate = "End date is required.";
        else if (recurEndDate < date) errs.recurEndDate = "Must be on or after the start date.";
      }
      if (recurEndsType === "after") {
        if (!Number.isInteger(Number(recurOccurrences)) || Number(recurOccurrences) < 1)
          errs.recurOccurrences = "Must be a positive whole number.";
      }
    }
    return errs;
  }, [title, date, startTimeStr, endTimeStr, repeatOption, recurInterval, recurFrequency, recurWeekDays, recurEndsType, recurEndDate, recurOccurrences]);

  const handleSubmit = useCallback(async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    let recurrence = null;
    if (repeatOption === "custom") {
      recurrence = {
        enabled: true,
        frequency: recurFrequency,
        interval: Number(recurInterval),
        weekDays: recurFrequency === "weekly" ? [...recurWeekDays] : [],
        monthlyDay: recurFrequency === "monthly" ? (dayOfMonthFromDateKey(date) ?? 1) : null,
        ends: {
          type: recurEndsType,
          endDate: recurEndsType === "on" ? recurEndDate : null,
          occurrences: recurEndsType === "after" ? Number(recurOccurrences) : null,
        },
      };
    }

    setIsSaving(true);
    try {
      await onCreate({ title: title.trim(), date, startTime, endTime, notes: notes.trim() || null, recurrence });
      onClose();
    } catch (err) {
      console.error("Failed to create event:", err);
      setErrors({ submit: "Failed to save. Please try again." });
    } finally {
      setIsSaving(false);
    }
  }, [validate, repeatOption, recurFrequency, recurInterval, recurWeekDays, recurEndsType, recurEndDate, recurOccurrences, title, date, startTime, endTime, notes, onCreate, onClose]);

  const toggleWeekDay = useCallback((day) => {
    setRecurWeekDays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]);
  }, []);

  if (!isOpen) return null;

  const monthlyDayNum = dayOfMonthFromDateKey(date);

  return (
    <div className="evtModalOverlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="evtModal">

        {/* Header */}
        <div className="evtModal__header">
          <span className="evtModal__title">Add Event</span>
          <button className="evtModal__closeBtn" onClick={onClose} type="button" aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="evtModal__body">

          {/* Quick-fill: Event Type + Name → auto-generates title */}
          <div className="evtModal__quickFillRow">
            <div className="evtModal__field" style={{ flex: 1 }}>
              <label className="evtModal__label">Event Type <span className="evtModal__labelHint">(optional)</span></label>
              <select
                className="evtModal__select"
                value={quickType}
                onChange={(e) => {
                  const t = e.target.value;
                  setQuickType(t);
                  if (t && quickName.trim()) {
                    setTitle(`${quickName.trim()} - ${t}`);
                    setTitleAutoFilled(true);
                  }
                }}
              >
                <option value="">— None / type manually —</option>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {quickType && (
              <div className="evtModal__field" style={{ flex: 1 }}>
                <label className="evtModal__label">Name</label>
                <input
                  className="evtModal__input"
                  type="text"
                  value={quickName}
                  onChange={(e) => {
                    const n = e.target.value;
                    setQuickName(n);
                    if (quickType) {
                      setTitle(n.trim() ? `${n.trim()} - ${quickType}` : quickType);
                      setTitleAutoFilled(true);
                    }
                  }}
                  placeholder="e.g. Abigail (11)"
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Title */}
          <div className="evtModal__field">
            <label className="evtModal__label">
              Title
              {titleAutoFilled && <span className="evtModal__labelHint"> (auto-generated — edit to override)</span>}
            </label>
            <input
              className="evtModal__input evtModal__input--title"
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setTitleAutoFilled(false); }}
              placeholder="Event title"
              autoFocus={!quickType}
            />
            {errors.title && <span className="evtModal__fieldError">{errors.title}</span>}
          </div>

          {/* Date */}
          <div className="evtModal__field">
            <label className="evtModal__label">Date</label>
            <input
              className="evtModal__input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            {errors.date && <span className="evtModal__fieldError">{errors.date}</span>}
          </div>

          {/* Start & End Time — side by side */}
          <div className="evtModal__timeRow">
            <div className="evtModal__field">
              <label className="evtModal__label">Start Time</label>
              <input
                className="evtModal__input"
                type="time"
                step="60"
                value={startTimeStr}
                onChange={(e) => setStartTimeStr(e.target.value)}
              />
            </div>
            <div className="evtModal__field">
              <label className="evtModal__label">End Time</label>
              <input
                className="evtModal__input"
                type="time"
                step="60"
                value={endTimeStr}
                onChange={(e) => setEndTimeStr(e.target.value)}
              />
              {errors.endTime && <span className="evtModal__fieldError">{errors.endTime}</span>}
            </div>
          </div>

          {/* Notes */}
          <div className="evtModal__field">
            <label className="evtModal__label">Notes <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span></label>
            <textarea
              className="evtModal__textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note…"
              rows={2}
            />
          </div>

          {/* Repeat */}
          <div className="evtModal__field">
            <label className="evtModal__label">Repeat</label>
            <select
              className="evtModal__select"
              value={repeatOption}
              onChange={(e) => setRepeatOption(e.target.value)}
            >
              <option value="never">Does not repeat</option>
              <option value="custom">Custom recurrence…</option>
            </select>
          </div>

          {/* Recurrence panel */}
          {repeatOption === "custom" && (
            <div className="evtModal__recurrencePanel">
              <div className="evtModal__recurrenceRow">
                <span className="evtModal__recurrenceLabel">Repeat every</span>
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={recurInterval}
                  onChange={(e) => setRecurInterval(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="evtModal__recurrenceIntervalInput"
                />
                <select
                  value={recurFrequency}
                  onChange={(e) => setRecurFrequency(e.target.value)}
                  className="evtModal__recurrenceFreqSelect"
                >
                  <option value="daily">day</option>
                  <option value="weekly">week</option>
                  <option value="monthly">month</option>
                </select>
              </div>
              {errors.recurInterval && <span className="evtModal__fieldError">{errors.recurInterval}</span>}

              {recurFrequency === "weekly" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span className="evtModal__recurrenceLabel">Repeat on</span>
                  <div className="evtModal__dayChips">
                    {DAY_CHIPS.map((chip) => (
                      <button
                        key={chip.key}
                        type="button"
                        className={`evtModal__dayChip${recurWeekDays.includes(chip.key) ? " evtModal__dayChip--active" : ""}`}
                        onClick={() => toggleWeekDay(chip.key)}
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                  {errors.recurWeekDays && <span className="evtModal__fieldError">{errors.recurWeekDays}</span>}
                </div>
              )}

              {recurFrequency === "monthly" && monthlyDayNum && (
                <div className="evtModal__recurrenceRow">
                  <span className="evtModal__recurrenceLabel">Repeats on</span>
                  <span className="evtModal__recurrenceStaticText">Monthly on day {monthlyDayNum}</span>
                </div>
              )}

              {/* Ends section */}
              <div className="evtModal__endsSection">
                <span className="evtModal__endsLabel">Ends</span>

                <label className="evtModal__radioRow">
                  <input type="radio" name="createRecurEnds" value="never" checked={recurEndsType === "never"} onChange={() => setRecurEndsType("never")} />
                  <span>Never</span>
                </label>

                <label className="evtModal__radioRow">
                  <input type="radio" name="createRecurEnds" value="on" checked={recurEndsType === "on"} onChange={() => setRecurEndsType("on")} />
                  <span>On</span>
                  {recurEndsType === "on" && (
                    <input type="date" value={recurEndDate} min={date} onChange={(e) => setRecurEndDate(e.target.value)} className="evtModal__radioDateInput" />
                  )}
                  {recurEndsType === "on" && errors.recurEndDate && <span className="evtModal__fieldError">{errors.recurEndDate}</span>}
                </label>

                <label className="evtModal__radioRow">
                  <input type="radio" name="createRecurEnds" value="after" checked={recurEndsType === "after"} onChange={() => setRecurEndsType("after")} />
                  <span>After</span>
                  {recurEndsType === "after" && (
                    <>
                      <input
                        type="number"
                        min="1"
                        max="999"
                        value={recurOccurrences}
                        onChange={(e) => setRecurOccurrences(Math.max(1, parseInt(e.target.value, 10) || 1))}
                        className="evtModal__recurrenceIntervalInput"
                      />
                      <span>occurrences</span>
                    </>
                  )}
                  {recurEndsType === "after" && errors.recurOccurrences && <span className="evtModal__fieldError">{errors.recurOccurrences}</span>}
                </label>
              </div>
            </div>
          )}

          {errors.submit && <div className="evtModal__submitError">{errors.submit}</div>}
        </div>

        {/* Footer */}
        <div className="evtModal__footer">
          <button className="evtModal__btn" type="button" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="evtModal__btn evtModal__btn--primary" type="button" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving…" : "Save Event"}
          </button>
        </div>
      </div>
    </div>
  );
}
